import EmailPassword from 'supertokens-web-js/recipe/emailpassword/index.js';
import Session from 'supertokens-web-js/recipe/session/index.js';
import type { User as SuperTokensUser } from 'supertokens-web-js/lib/build/types';
import type { LoginFormData, SignUpFormData, AuthResponse, User, SessionInfo } from '@/types/auth';
import type { IAuthService } from './IAuthService';
import { getLoggerWithPrefix } from '../shared';

/**
 * Convert SuperTokens User to our User type
 */
function convertUser(stUser: SuperTokensUser): User {
  return {
    id: stUser.id,
    email: stUser.emails[0] || '', // Use first email or empty string
    timeJoined: stUser.timeJoined,
    tenantIds: stUser.tenantIds
  };
}

/**
 * SuperTokens implementation of the authentication service
 * Contains the production authentication logic using SuperTokens
 */
export class SuperTokensAuthService implements IAuthService {
  logger = getLoggerWithPrefix('SuperTokens');

  /**
   * Sign in user with email and password
   */
  async signIn(formData: LoginFormData): Promise<AuthResponse> {
    try {
      const response = await EmailPassword.signIn({
        formFields: [
          { id: 'email', value: formData.email },
          { id: 'password', value: formData.password }
        ]
      });

      if (response.status === 'OK') {
        return {
          status: 'OK',
          user: convertUser(response.user)
        };
      }

      if (response.status === 'WRONG_CREDENTIALS_ERROR') {
        return {
          status: 'WRONG_CREDENTIALS_ERROR'
        };
      }

      if (response.status === 'FIELD_ERROR') {
        return {
          status: 'FIELD_ERROR',
          formFields: response.formFields
        };
      }

      throw new Error(`Unexpected response status: ${response.status}`);
    } catch (error) {
      this.logger.error('Sign in error:', error);
      throw error;
    }
  }

  /**
   * Sign up user with email and password
   */
  async signUp(formData: SignUpFormData): Promise<AuthResponse> {
    try {
      const response = await EmailPassword.signUp({
        formFields: [
          { id: 'email', value: formData.email },
          { id: 'password', value: formData.password }
        ]
      });

      if (response.status === 'OK') {
        return {
          status: 'OK',
          user: convertUser(response.user)
        };
      }

      if (response.status === 'FIELD_ERROR') {
        return {
          status: 'FIELD_ERROR',
          formFields: response.formFields
        };
      }

      if (response.status === 'SIGN_UP_NOT_ALLOWED') {
        // Map SIGN_UP_NOT_ALLOWED to a field error for consistency
        return {
          status: 'FIELD_ERROR',
          formFields: [{ id: 'email', error: 'Sign up not allowed for this email' }]
        };
      }

      // This should never happen with the current types, but keeping for safety
      throw new Error(`Unexpected response status`);
    } catch (error) {
      this.logger.error('Sign up error:', error);
      throw error;
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    try {
      await Session.signOut();
    } catch (error) {
      this.logger.error('Sign out error:', error);
      throw error;
    }
  }

  /**
   * Check if user session exists and is valid
   */
  async doesSessionExist(): Promise<boolean> {
    try {
      return await Session.doesSessionExist();
    } catch (error) {
      this.logger.error('Session check error:', error);
      return false;
    }
  }

  /**
   * Get current user information from session
   */
  async getUserInfo(): Promise<User | null> {
    try {
      const sessionExists = await Session.doesSessionExist();
      if (!sessionExists) {
        return null;
      }

      const userId = await Session.getUserId();
      const payload = await Session.getAccessTokenPayloadSecurely();

      return {
        id: userId,
        email: payload.email || '',
        timeJoined: payload.timeJoined || Date.now(),
        tenantIds: payload.tenantIds || []
      };
    } catch (error) {
      this.logger.error('Get user info error:', error);
      return null;
    }
  }

  /**
   * Get session information
   */
  async getSessionInfo(): Promise<SessionInfo | null> {
    try {
      const sessionExists = await Session.doesSessionExist();
      if (!sessionExists) {
        return null;
      }

      const userId = await Session.getUserId();
      const accessTokenPayload = await Session.getAccessTokenPayloadSecurely();
      const accessToken = await Session.getAccessToken();

      return {
        userId,
        accessTokenPayload,
        sessionHandle: accessToken || ''
      };
    } catch (error) {
      this.logger.error('Get session info error:', error);
      return null;
    }
  }

  /**
   * Refresh the current session
   */
  async refreshSession(): Promise<boolean> {
    try {
      const response = await Session.attemptRefreshingSession();
      return response;
    } catch (error) {
      this.logger.error('Refresh session error:', error);
      return false;
    }
  }

  /**
   * Check if session needs refresh and attempt refresh if needed
   */
  async ensureValidSession(): Promise<boolean> {
    try {
      const userId = await Session.getUserId();
      if (!userId) {
        return false;
      }

      // Try to get access token to check if session is valid
      try {
        await Session.getAccessToken();
        return true;
      } catch (tokenError) {
        // Session might need refresh
        this.logger.log('Session needs refresh, attempting refresh...', tokenError);
        return await this.refreshSession();
      }
    } catch {
      return false;
    }
  }

  /**
   * Get user ID from current session
   */
  async getCurrentUserId(): Promise<string | null> {
    try {
      const sessionExists = await Session.doesSessionExist();
      if (!sessionExists) {
        return null;
      }
      return await Session.getUserId();
    } catch (error) {
      this.logger.error('Get current user ID error:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated with valid session
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      return await this.ensureValidSession();
    } catch (error) {
      this.logger.error('Authentication check error:', error);
      return false;
    }
  }

  /**
   * Validate email format
   */
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate password strength
   */
  validatePassword(password: string): { isValid: boolean; message?: string } {
    if (password.length < 8) {
      return { isValid: false, message: 'Password must be at least 8 characters long' };
    }

    if (!/(?=.*[a-z])/.test(password)) {
      return { isValid: false, message: 'Password must contain at least one lowercase letter' };
    }

    if (!/(?=.*[A-Z])/.test(password)) {
      return { isValid: false, message: 'Password must contain at least one uppercase letter' };
    }

    if (!/(?=.*\d)/.test(password)) {
      return { isValid: false, message: 'Password must contain at least one number' };
    }

    return { isValid: true };
  }

  /**
   * Send password reset email to user
   */
  async sendPasswordResetEmail(email: string): Promise<{ status: 'OK' | 'EMAIL_DOES_NOT_EXIST_ERROR' }> {
    try {
      const response = await EmailPassword.sendPasswordResetEmail({
        formFields: [
          { id: 'email', value: email }
        ]
      });

      if (response.status === 'OK') {
        return { status: 'OK' };
      }

      if (response.status === 'PASSWORD_RESET_NOT_ALLOWED') {
        // Map to EMAIL_DOES_NOT_EXIST_ERROR for consistency
        return { status: 'EMAIL_DOES_NOT_EXIST_ERROR' };
      }

      // Handle any other status as email not found
      return { status: 'EMAIL_DOES_NOT_EXIST_ERROR' };
    } catch (error) {
      this.logger.error('Send password reset email error:', error);
      throw error;
    }
  }

  /**
   * Submit new password with reset token
   */
  async submitNewPassword(token: string, newPassword: string): Promise<{ status: 'OK' | 'RESET_PASSWORD_INVALID_TOKEN_ERROR' | 'FIELD_ERROR'; formFields?: Array<{ id: string; error: string }> }> {
    try {
      const response = await EmailPassword.submitNewPassword({
        formFields: [
          { id: 'password', value: newPassword }
        ]
      });

      if (response.status === 'OK') {
        return { status: 'OK' };
      }

      if (response.status === 'RESET_PASSWORD_INVALID_TOKEN_ERROR') {
        return { status: 'RESET_PASSWORD_INVALID_TOKEN_ERROR' };
      }

      if (response.status === 'FIELD_ERROR') {
        return {
          status: 'FIELD_ERROR',
          formFields: response.formFields
        };
      }

      // Handle any other response status
      return { status: 'RESET_PASSWORD_INVALID_TOKEN_ERROR' };
    } catch (error) {
      this.logger.error('Submit new password error:', error);
      throw error;
    }
  }
}